// useHeadphoneMonitor.js
//
// Best-effort headphone connection monitor for desktop Chrome/Edge on
// macOS and Windows.
//
// IMPORTANT — read before tuning thresholds:
// There is no Web API that reports "physical headphones are plugged in" as
// a hard fact. The only signal available is
// navigator.mediaDevices.enumerateDevices() — specifically the *labels*
// Chrome/Edge assign to audiooutput devices (only populated once mic/cam
// permission has been granted). That's why this hook always surfaces
// `needsManualConfirmation` for the cases it can't resolve confidently
// (e.g. permission blocked), instead of silently guessing.
//
// DETECTION RULE (deliberately brand-agnostic):
// We don't try to pattern-match every headphone brand/model — that list is
// never complete. Instead: ANY real output device that is not the
// machine's built-in speaker counts as "headphones connected." This covers
// every type equally — wired 3.5mm, USB/USB-C, Bluetooth, AirPods, generic
// unbranded devices, everything — because none of them need to be
// recognized by name, only recognized as "not the built-in speaker."
//
// Two categories still need to be excluded so this stays accurate:
//  1. Built-in/internal speakers — "Speakers (Realtek...)", "MacBook Pro
//     Speakers", "Built-in Output", "Display Audio" — these are the one
//     thing we're trying to detect the *absence* of a preference for.
//  2. Virtual/software output devices — "Teams Audio", "Zoom Audio",
//     "VB-Cable", "Voicemeeter", "Stereo Mix" — these aren't physical
//     hardware at all, so treating them as "headphones" would let someone
//     pass the check with no headphones on.
// Everything else — any label, any brand, wired or wireless — counts.

import { useCallback, useEffect, useRef, useState } from "react";

// macOS labels any device wired through the analog jack as
// "External Headphones (Built-in)" / "External Microphone (Built-in)" — the
// "(Built-in)" there means "using the Mac's built-in audio controller," NOT
// "this is the internal speaker." A bare /built-?in/i match would wrongly
// classify wired headphones as the built-in speaker because of that one
// word. Anything starting with "External" is authoritative and always wins,
// checked before the built-in patterns below.
//
// Separately, on Windows, Chrome/Edge often label EVERY output device with
// the generic role name "Speakers" regardless of whether it's onboard or a
// plugged-in dongle — e.g. "Speakers (USB Audio Device)" for a USB-C
// headphone adapter, not just "Speakers (Realtek(R) Audio)" for the actual
// built-in chip. So "Speakers (...)" alone isn't a safe built-in signal on
// Windows. A "USB" or "Type-C" mention in the label is a strong positive
// signal it's an external dongle, so it overrides the built-in match too.
const EXTERNAL_OVERRIDE_PATTERNS = [
  /^external/i,
  /external headphone/i,
  /external speaker/i,
  /\busb\b/i,
  /\btype[\s-]?c\b/i,
];

const BUILTIN_SPEAKER_PATTERNS = [
  /^internal speakers?/i,
  /^built-?in\s*(output|speakers?)/i,
  /macbook.*speaker/i,
  /imac.*speaker/i,
  /^speakers?(\s|\(|$)/i, // "Speakers (Realtek High Definition Audio)"
  /display audio/i,
  /^default(\s*-\s*)?speaker/i,
  /realtek.*speaker/i,
  /laptop speaker/i,
];

const VIRTUAL_DEVICE_PATTERNS = [
  /virtual/i,
  /\bteams\b/i,
  /\bzoom\b/i,
  /vb-?cable/i,
  /voicemeeter/i,
  /stereo mix/i,
  /cable (input|output)/i,
  /loopback/i,
  /soundflower/i,
  /blackhole/i,
  /obs/i,
];

// Windows machines commonly list onboard audio jacks/ports that ALWAYS
// appear in the device list whether or not anything is physically plugged
// into them — e.g. Realtek's optical/digital output, or an HDMI port that
// supports audio even with no cable connected. These aren't "real"
// external devices in the sense we care about, so we exclude them the
// same way virtual devices are excluded, rather than letting them
// falsely count as "headphones connected."
const PHANTOM_OUTPUT_PATTERNS = [
  /digital output/i,
  /s\/?pdif/i,
  /optical/i,
  /toslink/i,
  /\bhdmi\b/i,
  /line\s?out/i,
];

const DEBOUNCE_MS = 400;
// Belt-and-braces poll: `devicechange` is well supported in Chrome/Edge on
// macOS & Windows, but some Bluetooth driver stacks are slow or inconsistent
// about firing it on pair/unpair. A cheap periodic re-check guarantees
// self-correction within a few seconds even then.
const POLL_MS = 3000;

function isBuiltinSpeaker(label) {
  if (!label) return false;
  if (EXTERNAL_OVERRIDE_PATTERNS.some((re) => re.test(label))) return false;
  return BUILTIN_SPEAKER_PATTERNS.some((re) => re.test(label));
}

function classifyOutputs(devices) {
  // Chrome/Edge report the same physical device twice under "default" /
  // "communications" deviceIds plus its real groupId. Dedupe by groupId (or
  // label if groupId is missing) so one headset doesn't get double-counted.
  const outputs = devices.filter((d) => d.kind === "audiooutput");
  const seen = new Set();
  const deduped = outputs.filter((d) => {
    const key = d.groupId || d.label || d.deviceId;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const hasUsableLabels = deduped.some((d) => d.label && d.label.trim());

  const realDevices = deduped.filter(
    (d) =>
      !VIRTUAL_DEVICE_PATTERNS.some((re) => re.test(d.label)) &&
      !PHANTOM_OUTPUT_PATTERNS.some((re) => re.test(d.label)),
  );

  const builtinMatches = realDevices.filter((d) => isBuiltinSpeaker(d.label));

  // Anything real, labeled, and not built-in = external audio output,
  // regardless of brand or connection type (wired/USB/Bluetooth).
  const externalMatches = realDevices.filter(
    (d) => d.label?.trim() && !isBuiltinSpeaker(d.label),
  );

  return { outputs: realDevices, hasUsableLabels, externalMatches, builtinMatches };
}

/**
 * @param {Object} opts
 * @param {boolean} opts.enabled
 * @param {Function} [opts.onConnect]
 * @param {Function} [opts.onDisconnect]
 * @returns {{
 *   isHeadphoneConnected: boolean,
 *   needsManualConfirmation: boolean,
 *   permissionDenied: boolean,
 *   confirmManually: () => void,
 * }}
 */
export default function useHeadphoneMonitor({ enabled = true, onConnect, onDisconnect } = {}) {
  const [isHeadphoneConnected, setIsHeadphoneConnected] = useState(false);
  const [needsManualConfirmation, setNeedsManualConfirmation] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const wasConnectedRef = useRef(false);
  const manualOverrideRef = useRef(sessionStorage.getItem("headphoneManualConfirm") === "true");
  const debounceTimer = useRef(null);
  const pollTimer = useRef(null);
  const streamRef = useRef(null);
  const mountedRef = useRef(true);

  const fireTransition = useCallback(
    (connected) => {
      if (connected === wasConnectedRef.current) return;
      wasConnectedRef.current = connected;
      if (connected) onConnect?.();
      else onDisconnect?.();
    },
    [onConnect, onDisconnect],
  );

  // Labels are blank until getUserMedia permission has been granted at
  // least once on this origin. In a meeting app the mic is almost always
  // already granted before this runs, so this is usually a no-op.
  const ensureLabelPermission = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasLabels = devices.some((d) => d.kind === "audiooutput" && d.label?.trim());
      if (hasLabels) return true;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // We only needed the permission grant, not an active mic stream —
      // the app manages its own mic stream elsewhere.
      stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      return true;
    } catch {
      if (mountedRef.current) setPermissionDenied(true);
      return false;
    }
  }, []);

  const evaluate = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      if (mountedRef.current) {
        setIsHeadphoneConnected(false);
        setNeedsManualConfirmation(true);
      }
      fireTransition(false);
      return;
    }

    if (manualOverrideRef.current) {
      if (mountedRef.current) {
        setIsHeadphoneConnected(true);
        setNeedsManualConfirmation(false);
      }
      fireTransition(true);
      return;
    }

    await ensureLabelPermission();

    let devices;
    try {
      devices = await navigator.mediaDevices.enumerateDevices();
    } catch {
      if (mountedRef.current) setNeedsManualConfirmation(true);
      return;
    }

    const { hasUsableLabels, externalMatches, builtinMatches } = classifyOutputs(devices);

    // No labels at all (permission blocked/denied) -> nothing to infer from.
    if (!hasUsableLabels) {
      if (mountedRef.current) {
        setIsHeadphoneConnected(false);
        setNeedsManualConfirmation(true);
      }
      fireTransition(false);
      return;
    }

    // Any real, non-built-in, non-virtual output device present = treated
    // as headphones connected. Brand/type agnostic by design — wired, USB,
    // Bluetooth, AirPods, generic/unbranded devices all count equally.
    if (externalMatches.length > 0) {
      if (mountedRef.current) {
        setIsHeadphoneConnected(true);
        setNeedsManualConfirmation(false);
      }
      fireTransition(true);
      return;
    }

    // Only built-in speaker(s) visible, nothing external -> not connected.
    if (builtinMatches.length > 0) {
      if (mountedRef.current) {
        setIsHeadphoneConnected(false);
        setNeedsManualConfirmation(false);
      }
      fireTransition(false);
      return;
    }

    // Neither external nor recognizably built-in (unusual/unlabeled setup)
    // — don't guess, ask the user.
    if (mountedRef.current) {
      setIsHeadphoneConnected(false);
      setNeedsManualConfirmation(true);
    }
    fireTransition(false);
  }, [ensureLabelPermission, fireTransition]);

  const scheduleEvaluate = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(evaluate, DEBOUNCE_MS);
  }, [evaluate]);

  const confirmManually = useCallback(() => {
    manualOverrideRef.current = true;
    sessionStorage.setItem("headphoneManualConfirm", "true");
    setIsHeadphoneConnected(true);
    setNeedsManualConfirmation(false);
    fireTransition(true);
  }, [fireTransition]);

  useEffect(() => {
    mountedRef.current = true;

    if (!enabled) {
      setIsHeadphoneConnected(false);
      setNeedsManualConfirmation(false);
      return;
    }

    evaluate();

    const handleDeviceChange = () => scheduleEvaluate();
    navigator.mediaDevices?.addEventListener?.("devicechange", handleDeviceChange);
    pollTimer.current = setInterval(evaluate, POLL_MS);

    return () => {
      mountedRef.current = false;
      navigator.mediaDevices?.removeEventListener?.("devicechange", handleDeviceChange);
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      if (pollTimer.current) clearInterval(pollTimer.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { isHeadphoneConnected, needsManualConfirmation, permissionDenied, confirmManually };
}